'use client';

/* eslint-disable @typescript-eslint/naming-convention */
import { Geometry, Mesh, Program, Renderer, Texture, Vec2, Vec4 } from 'ogl';
import { useEffect, useRef } from 'react';

import { Controller } from './GlHelpers';

const vertex = /* glsl */ `attribute vec2 uv;
attribute vec2 position;
varying vec2 vUv;

void main() {
    vUv = uv;
    gl_Position = vec4(position, 0., 1.);
}`;

const fragment = /* glsl */ `
precision mediump float;
  varying vec2 vUv;
  uniform sampler2D uTexA;
  uniform sampler2D uTexB;
  uniform vec3 uMouse;
  uniform vec2 uResolution;
  uniform vec2 uTexBResolution;
  uniform vec4 uColor;

  vec2 aspect(vec2 st, vec2 s) {
    st.x = st.x * (s.x / s.y);
    return st;
  } 

  vec2 cover(vec2 screenResolution, vec2 imageResolution) {
    vec2 ratio = vec2(
      min((screenResolution.x / screenResolution.y) / (imageResolution.x / imageResolution.y), 1.0),
      min((screenResolution.y / screenResolution.x) / (imageResolution.y / imageResolution.x), 1.0)
    );

    return vec2(
      vUv.x * ratio.x + (1.0 - ratio.x) * 0.5,
      vUv.y * ratio.y + (1.0 - ratio.y) * 0.5
   );
  }

  float center(float x) { return x * 2.0 - 1.0; }
  vec2  center(vec2 v) { return v * 2.0 - 1.0; }
  vec3  center(vec3 v) { return v * 2.0 - 1.0; }

  void main() {
    vec2 uv = vUv;
    vec2 maskUV = vUv;
    vec2 m = uMouse.xy;
    m.x *= -1.;

    m.x += 0.5;

    maskUV += m;

    maskUV = aspect(maskUV, uResolution);

    float mask = smoothstep(0.5, 1., 1.-distance(maskUV,vec2(1.)));
    vec4 overlay = texture2D(uTexA, vUv/2.) ;
    vec4 roverlay = vec4(1., 1., 1., overlay.a);

    vec2 videoUV = cover(uResolution, uTexBResolution);
  
    vec4 video = texture2D(uTexB, videoUV);

    gl_FragColor = ((video*(1. - uColor.a)) + uColor) + (roverlay * mask * overlay.a);
  }
`;

const noVideoFragment = /* glsl */ `
precision mediump float;
  varying vec2 vUv;
  uniform sampler2D uTexA;
  uniform sampler2D uTexB;
  uniform vec3 uMouse;
  uniform vec2 uResolution;
  uniform vec2 uTexBResolution;
  uniform vec4 uColor;

  vec2 aspect(vec2 st, vec2 s) {
    st.x = st.x * (s.x / s.y);
    return st;
  } 
  float center(float x) { return x * 2.0 - 1.0; }
  vec2  center(vec2 v) { return v * 2.0 - 1.0; }
  vec3  center(vec3 v) { return v * 2.0 - 1.0; }

  void main() {
    vec2 uv = vUv;
    vec2 maskUV = vUv;
    vec2 m = uMouse.xy;
    m.x *= -1.;

    m.x += 0.5;

    maskUV += m;

    maskUV = aspect(maskUV, uResolution);

    float mask = smoothstep(0.6, 1., 1.-distance(maskUV,vec2(1.)));
    vec4 overlay = texture2D(uTexA, uv/2.);
    vec4 roverlay = vec4(0., 0., 0., overlay.a);

    gl_FragColor = roverlay * mask;

  }
`;

function loadTexture(gl, path = '/assets/placeholder.webp'): Texture {
  const texture = new Texture(gl, {
    generateMipmaps: false,
    wrapS: gl.MIRRORED_REPEAT,
    wrapT: gl.MIRRORED_REPEAT,
    width: 128,
    height: 128,
  });
  if (path) {
    const img1 = new Image();
    img1.src = path;
    img1.crossOrigin = 'anonymous';
    img1.onload = () => {
      texture.image = img1;
      texture.width = img1.width;
      texture.height = img1.height;
      texture.needsUpdate = true;
    };
  }
  return texture;
}

export default function WebGL2Component({
  overlay = '/assets/topographical-map-4.png',
  video = null,
  image = '/assets/placeholder.webp',
  color = [1, 1, 1, 1],
}: {
  overlay?: string;
  video?: string;
  image?: string;
  color?: [number, number, number, number];
}) {
  const ref = useRef();
  const containerRef = useRef<HTMLDivElement>();
  const vid = useRef<HTMLVideoElement>();
  const rendererRef = useRef<Renderer>();
  const resolutionRef = useRef(new Vec2(0, 0));
  const controllerRef = useRef<Controller>();
  const geometryRef = useRef<Geometry>();
  const videoTextureRef = useRef<Texture>();
  const overlayImageRef = useRef<Texture>();
  const videoTextureSizeRef = useRef(new Vec2(0, 0));
  const programRef = useRef<Program>();
  const meshRef = useRef<Mesh>();

  useEffect(() => {
    if (!ref.current && !vid.current) return () => {};

    controllerRef.current = new Controller();

    const controller = controllerRef.current;

    rendererRef.current = new Renderer({
      canvas: ref.current,
      alpha: true,
    });

    const renderer = rendererRef.current;

    const { gl } = renderer;
    gl.clearColor(0, 0, 0, 0);

    geometryRef.current = new Geometry(gl, {
      position: { size: 2, data: new Float32Array([-1, -1, 3, -1, -1, 3]) },
      uv: { size: 2, data: new Float32Array([0, 0, 2, 0, 0, 2]) },
    });

    const resolution = resolutionRef.current;

    function resize() {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        renderer.setSize(rect.width, rect.height);
        resolution.x = gl.canvas.width;
        resolution.y = gl.canvas.height;
      }
    }

    window.addEventListener('resize', resize, false);

    resize();

    const geometry = geometryRef.current;

    overlayImageRef.current = loadTexture(gl, overlay);

    if (video) {
      videoTextureRef.current = new Texture(gl, {
        generateMipmaps: false,
        width: 1280,
        height: 720,
      });
      videoTextureRef.current.image = vid.current;
      videoTextureRef.current.needsUpdate = true;
    } else {
      videoTextureRef.current = loadTexture(gl, image);
    }

    programRef.current = new Program(gl, {
      vertex,
      fragment: video || image ? fragment : noVideoFragment,
      uniforms: {
        uMouse: { value: controller.mousePos },
        uColor: { value: new Vec4(color[0], color[1], color[2], color[3]) },
        uResolution: { value: resolution },
        uTexA: { value: overlayImageRef.current },
        uTexB: { value: videoTextureRef.current },
        uTexBResolution: {
          value: videoTextureSizeRef.current,
        },
      },
      cullFace: false,
    });

    const program = programRef.current;

    meshRef.current = new Mesh(gl, { geometry, program });

    let frame;

    function update() {
      frame = requestAnimationFrame(update);

      controller.update();

      videoTextureSizeRef.current.x = videoTextureRef.current.width;
      videoTextureSizeRef.current.y = videoTextureRef.current.height;

      if (vid.current?.readyState >= vid.current?.HAVE_ENOUGH_DATA) {
        videoTextureRef.current.needsUpdate = true;
      }

      program.uniforms.uMouse.value = controller.mousePos;
      program.uniforms.uTexA.value = overlayImageRef.current;
      program.uniforms.uTexB.value = videoTextureRef.current;
      program.uniforms.uResolution.value = resolution;
      program.uniforms.uTexBResolution.value = videoTextureSizeRef.current;

      renderer.render({ scene: meshRef.current });
    }

    frame = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  });

  return (
    <div ref={containerRef} className="relative h-screen w-screen">
      <canvas
        ref={ref}
        className="absolute inset-0 z-10 size-full object-cover"
      ></canvas>
      <video
        ref={vid}
        src={video}
        loop={true}
        muted={true}
        playsInline={true}
        crossOrigin="anonymous"
        autoPlay={true}
        className="absolute inset-0 z-0 size-full object-cover"
      ></video>
    </div>
  );
}
